/**
 * Builds the Hubs & Infrastructure screen's view of each airport: tier and
 * progress, running costs and MRO income, and every buildable project priced
 * against the airline as it stands. Pure: the engine supplies the AirlineState
 * and the list of airports to show.
 */

import type { AirportId } from '../models';
import { BASE_TIER_REQUIREMENTS, MRO_MAX_LEASED_BAYS } from './hubConstants';
import {
  calculateHubInfrastructureImpact,
  infrastructureAt,
  peakSlotsHeld,
  quoteInfrastructureUpgrade,
  weeklyDeparturesAt,
} from './hubEngine';
import { BASE_TIERS, LOUNGE_TIERS } from './types';
import type { AirlineState, BaseTier, BaseTierProgress, HubAirportView, HubProjectQuote, InfrastructureProject } from './types';

/** Stable key for a project: the React key and the lookup the UI uses for its buttons. */
export function hubProjectKey(project: InfrastructureProject): string {
  switch (project.kind) {
    case 'BASE_TIER':
      return `BASE_TIER:${project.tier}`;
    case 'MRO_LEASING':
      return `MRO_LEASING:${project.leasedBays}`;
    case 'LOUNGE':
      return `LOUNGE:${project.tier}`;
    case 'LINE_MAINTENANCE':
    case 'HEAVY_HANGAR':
    case 'OWNED_GROUND_HANDLING':
    case 'IN_HOUSE_CATERING':
      return project.kind;
    default: {
      const exhaustive: never = project;
      throw new Error(`Unhandled infrastructure project ${JSON.stringify(exhaustive)}`);
    }
  }
}

const LOUNGE_LABELS: Readonly<Record<(typeof LOUNGE_TIERS)[number], string>> = {
  THIRD_PARTY_CONTRACT: 'Contract lounge',
  DEDICATED_BUSINESS_LOUNGE: 'Business lounge',
  FLAGSHIP_FIRST_LOUNGE: 'Flagship First lounge',
};

/** The tier above `tier`, or null at the top of the ladder. */
export function nextBaseTier(tier: BaseTier): BaseTier | null {
  const index = BASE_TIERS.indexOf(tier);
  return index < 0 || index >= BASE_TIERS.length - 1 ? null : (BASE_TIERS[index + 1] ?? null);
}

function labelledProjects(airline: AirlineState, airportId: AirportId): readonly { readonly label: string; readonly project: InfrastructureProject }[] {
  const infra = infrastructureAt(airline, airportId);
  const projects: { label: string; project: InfrastructureProject }[] = [];

  const next = nextBaseTier(infra.baseTier);
  if (next !== null) projects.push({ label: `Step up to ${next.replace(/_/g, ' ').toLowerCase()}`, project: { kind: 'BASE_TIER', tier: next } });

  projects.push({ label: 'Line maintenance', project: { kind: 'LINE_MAINTENANCE' } });
  projects.push({ label: 'Heavy hangar (C and D checks)', project: { kind: 'HEAVY_HANGAR' } });
  if (infra.leasedMroBays < MRO_MAX_LEASED_BAYS) {
    const leasedBays = infra.leasedMroBays + 1;
    projects.push({ label: `Lease MRO bay ${leasedBays}`, project: { kind: 'MRO_LEASING', leasedBays } });
  }
  projects.push({ label: 'Owned ground handling', project: { kind: 'OWNED_GROUND_HANDLING' } });
  projects.push({ label: 'In-house catering', project: { kind: 'IN_HOUSE_CATERING' } });
  for (const tier of LOUNGE_TIERS) projects.push({ label: LOUNGE_LABELS[tier], project: { kind: 'LOUNGE', tier } });

  return projects;
}

function quoteProjects(airline: AirlineState, airportId: AirportId): readonly HubProjectQuote[] {
  return labelledProjects(airline, airportId).map(({ label, project }) => {
    const priced = quoteInfrastructureUpgrade(airline, airportId, project);
    return {
      key: hubProjectKey(project),
      label,
      project,
      quote: priced.ok ? { ok: true, capexUSD: priced.capexUSD } : { ok: false, reasons: priced.reasons },
    };
  });
}

function progressTowards(airline: AirlineState, airportId: AirportId, tier: BaseTier | null): BaseTierProgress | null {
  if (tier === null) return null;
  const requirement = BASE_TIER_REQUIREMENTS[tier];
  return {
    tier,
    minWeeklyDepartures: requirement.minWeeklyDepartures,
    weeklyDepartures: weeklyDeparturesAt(airline, airportId),
    minPeakSlotsHeld: requirement.minPeakSlotsHeld,
    peakSlotsHeld: peakSlotsHeld(airline, airportId),
    stepUpCapexUSD: requirement.stepUpCapexUSD,
  };
}

/** The hub card for one airport. */
export function buildHubAirportView(airline: AirlineState, airportId: AirportId): HubAirportView {
  const infrastructure = infrastructureAt(airline, airportId);
  const impact = calculateHubInfrastructureImpact(airportId, airline);
  return {
    airportId,
    baseTier: infrastructure.baseTier,
    infrastructure,
    weeklyDepartures: weeklyDeparturesAt(airline, airportId),
    peakSlotsHeld: peakSlotsHeld(airline, airportId),
    competingCarriers: airline.competingCarriersByAirport[airportId] ?? 0,
    nextTier: progressTowards(airline, airportId, nextBaseTier(infrastructure.baseTier)),
    dailyOperatingCostUSD: impact.dailyOperatingCostUSD,
    dailyMroLeaseRevenueUSD: impact.dailyMroLeaseRevenueUSD,
    projects: quoteProjects(airline, airportId),
  };
}

/** Hub cards for the given airports, in the order given. */
export function buildHubAirportViews(airline: AirlineState, airportIds: readonly AirportId[]): HubAirportView[] {
  return airportIds.map((airportId) => buildHubAirportView(airline, airportId));
}
